import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  TableContainer,
  Table as MUITable,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import { formatDate, formatMoney, parseEuroNumber } from '../utils/helper';
import PageShell from '../components/PageShell';
import StatusChip from '../components/StatusChip';
import { useQuery } from '@tanstack/react-query';
import { getAllCommissions, useApproveCommission, useDeclineCommission } from '../queries/commissions';
import { COMMISSION_STATUS, USER_ROLES } from '../constants';
import { toast } from 'react-toastify';
import CommissionHistoryModal from '../components/CommissionHistoryModal';

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const ClientCommissionOverview = () => {
  const { data: commissions, isLoading, isRefetching, refetch } = useQuery(getAllCommissions({}));

  const { mutate: approveCommission, isPending: isApproving } = useApproveCommission();
  const { mutate: declineCommission, isPending: isDeclining } = useDeclineCommission();

  const [data, setData] = useState({});
  const [expandedYears, setExpandedYears] = useState([new Date().getFullYear()]);
  const [dialog, setDialog] = useState({
    open: false,
    row: null,
    reason: '',
    cleared: false,
    backup: '',
    mode: 'edit',
  });
  const [historyDialog, setHistoryDialog] = useState({ open: false, row: null });

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = months[currentDate.getMonth()];

  useEffect(() => {
    if (!isLoading && !isRefetching) {
      const groupedData =
        commissions?.reduce((acc, item) => {
          let year = currentYear;
          let monthName = currentMonth;
          if (item.locked) {
            const bucketDate = new Date(item.approvedAt || item.submittedAt);
            const month = bucketDate.getUTCMonth();
            year = bucketDate.getUTCFullYear();
            monthName = months[month];
          }
          if (!acc[year]) {
            acc[year] = {};
          }
          if (!acc[year][monthName]) {
            acc[year][monthName] = [];
          }
          acc[year][monthName].push(item);

          return acc;
        }, {}) || {};
      setData(groupedData);
    }
  }, [isLoading, isRefetching, commissions, currentYear, currentMonth]);

  const toggleYear = (year) => {
    setExpandedYears((prev) => (prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]));
  };

  const openDeclineDialog = (row) => {
    setDialog({
      open: true,
      row,
      reason: row.declineReason || '',
      cleared: false,
      backup: row.declineReason || '',
      mode: 'decline',
    });
  };

  const openEditDialog = (row) => {
    setDialog({
      open: true,
      row,
      reason: row.declineReason || '',
      cleared: false,
      backup: row.declineReason || '',
      mode: 'edit',
    });
  };

  const handleApprove = () => {
    const { row } = dialog;
    if (row?._id) {
      approveCommission(
        { id: row._id },
        {
          onSuccess: () => {
            refetch();
            closeDialog();
            toast('Approved commission successfully!', { type: 'success' });
          },
          onError: (error) => {
            const message =
              error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              'Failed to approve commission.';
            toast(message, { type: 'error' });
          },
        },
      );
    }
  };

  const handleDecline = () => {
    const { row, reason } = dialog;
    if (!reason.trim()) return;
    if (row?._id) {
      declineCommission(
        { id: row._id, data: { declineReason: reason } },
        {
          onSuccess: () => {
            refetch();
            closeDialog();
            toast('Declined commission successfully!', { type: 'success' });
          },
          onError: (error) => {
            const message =
              error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              'Failed to decline commission.';
            toast(message, { type: 'error' });
          },
        },
      );
    }
  };

  const closeDialog = () => {
    setDialog({
      open: false,
      row: null,
      reason: '',
      cleared: false,
      backup: '',
      mode: 'edit',
    });
  };

  const clearReason = () => {
    setDialog((prev) => ({ ...prev, cleared: true, reason: '' }));
  };

  const restoreReason = () => {
    setDialog((prev) => ({ ...prev, cleared: false, reason: prev.backup }));
  };

  const truncate = (text) => (text.length > 15 ? text.slice(0, 15) + '...' : text);

  const handleApproveDirect = (row) => {
    if (row?._id) {
      approveCommission(
        { id: row._id },
        {
          onSuccess: () => {
            refetch();
            toast('Approved commission successfully!', { type: 'success' });
          },
          onError: (error) => {
            const message =
              error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              'Failed to approve commission.';
            toast(message, { type: 'error' });
          },
        },
      );
    }
  };

  const years = [currentYear, currentYear - 1];
  const lightColor1 = '#f0f4ff';
  const lightColor2 = '#f4f6f8';

  const totalGlobalCommission = Object.values(data)
    .flatMap((yearData) => Object.values(yearData).flat())
    .reduce((sum, entry) => {
      const int = parseEuroNumber(entry.commission);
      const dec = parseInt(entry.cents || '0', 10);
      return sum + int + dec / 100;
    }, 0);

  return (
    <>
      <PageShell
        title='Commissions'
        actions={
          <Typography variant='h6' color='text.secondary' fontWeight='normal'>
            {formatMoney(totalGlobalCommission)}
          </Typography>
        }
      >
        {years.map((year) => {
          const yearTotal = Object.values(data[year] || {})
            .flat()
            .reduce((sum, row) => {
              const int = parseEuroNumber(row.commission);
              const dec = parseInt(row.cents || '0', 10);
              return sum + int + dec / 100;
            }, 0);

          const monthsForYear =
            year === currentYear
              ? [currentMonth, ...months.slice(0, months.indexOf(currentMonth)).reverse()]
              : [...months].reverse();

          return (
            <Box key={year} mb={5}>
              <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
                <Box display='flex' alignItems='center' gap={1}>
                  <Typography variant='h6' fontWeight='bold'>
                    {year}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {formatMoney(yearTotal)}
                  </Typography>
                </Box>
                <IconButton onClick={() => toggleYear(year)}>
                  {expandedYears.includes(year) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={expandedYears.includes(year)} timeout={300}>
                {monthsForYear.map((month, idx) => {
                  const monthData = data[year]?.[month] || [];
                  const monthTotal = monthData.reduce((sum, row) => {
                    const int = parseEuroNumber(row.commission);
                    const dec = parseInt(row.cents || '0', 10);
                    return sum + int + dec / 100;
                  }, 0);

                  const isEditable = year === currentYear && month === currentMonth;

                  return (
                    <Box
                      key={month}
                      mt={2}
                      p={2}
                      sx={{
                        backgroundColor: idx % 2 === 0 ? lightColor1 : lightColor2,
                        borderRadius: 2,
                        mb: 3,
                        transition: 'box-shadow 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        },
                      }}
                    >
                      <Box display='flex' alignItems='center' justifyContent='space-between' mb={2}>
                        <Box display='flex' alignItems='center' gap={1}>
                          <CalendarMonthIcon fontSize='small' />
                          <Typography variant='subtitle1' fontWeight='bold'>
                            {month}
                          </Typography>
                          <Typography variant='body2' color='text.secondary'>
                            {formatMoney(monthTotal)}
                          </Typography>
                        </Box>
                      </Box>
                      <TableContainer>
                        <MUITable>
                          <TableHead>
                            <TableRow>
                              {['Lead Agent', 'Prospect', 'Event', 'Commission', 'Submitted', 'Status', 'History'].map(
                                (h) => (
                                  <TableCell key={h} sx={{ whiteSpace: 'nowrap' }}>
                                    {h}
                                  </TableCell>
                                ),
                              )}
                              {isEditable ? (
                                <TableCell sx={{ whiteSpace: 'nowrap', paddingLeft: 3 }}>Actions</TableCell>
                              ) : null}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {monthData.length ? (
                              monthData.map((row) => {
                                const intPart = parseEuroNumber(row.commission);
                                const decPart = parseInt(row.cents || '0', 10);
                                const fullAmount = intPart + decPart / 100;
                                const formattedAmount = formatMoney(fullAmount);

                                return (
                                  <TableRow key={row._id}>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                      <Tooltip title={`${row.leadAgent?.firstName} ${row.leadAgent?.lastName}`} arrow>
                                        <span>
                                          {truncate(`${row.leadAgent?.firstName} ${row.leadAgent?.lastName}`)}
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                      <Tooltip title={row.prospect}>
                                        <span>{truncate(row.prospect)}</span>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                      <Tooltip
                                        title={row.event === 'Other' && row.otherEvent ? row.otherEvent : row.event}
                                        arrow
                                      >
                                        <span>
                                          {truncate(
                                            row.event === 'Other' && row.otherEvent ? row.otherEvent : row.event,
                                          )}
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                      <Box
                                        display='inline-flex'
                                        alignItems='center'
                                        justifyContent='space-between'
                                        width='100%'
                                        gap={1}
                                      >
                                        {formattedAmount}
                                        {row.notes && (
                                          <Tooltip title={row.notes}>
                                            <InfoIcon fontSize='small' color='action' />
                                          </Tooltip>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.submittedAt)}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                      <StatusChip
                                        status={row.status}
                                        icon={isApproving || isDeclining ? <CircularProgress size={18} /> : null}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                      <Link
                                        component='button'
                                        type='button'
                                        underline='hover'
                                        onClick={() => setHistoryDialog({ open: true, row })}
                                      >
                                        Show
                                      </Link>
                                    </TableCell>
                                    {isEditable ? (
                                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {row.status === COMMISSION_STATUS.PENDING && (
                                          <>
                                            <Tooltip title='Approve'>
                                              <IconButton
                                                disabled={isApproving || isDeclining}
                                                onClick={() => handleApproveDirect(row)}
                                              >
                                                {isApproving ? (
                                                  <CircularProgress size={18} />
                                                ) : (
                                                  <CheckCircleIcon color='success' />
                                                )}
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title='Decline'>
                                              <IconButton
                                                disabled={isApproving || isDeclining}
                                                onClick={() => openDeclineDialog(row)}
                                              >
                                                {isDeclining ? (
                                                  <CircularProgress size={18} />
                                                ) : (
                                                  <CancelIcon color='error' />
                                                )}
                                              </IconButton>
                                            </Tooltip>
                                          </>
                                        )}
                                        {row.status !== COMMISSION_STATUS.PENDING && (
                                          <Tooltip title='Edit Decision'>
                                            <IconButton onClick={() => openEditDialog(row)}>
                                              <EditIcon />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </TableCell>
                                    ) : null}
                                  </TableRow>
                                );
                              })
                            ) : (
                              <TableRow>
                                <TableCell colSpan={8} align='center'>
                                  No commissions submitted.
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </MUITable>
                      </TableContainer>
                    </Box>
                  );
                })}
              </Collapse>
            </Box>
          );
        })}
      </PageShell>

      <CommissionHistoryModal
        open={historyDialog.open}
        onClose={() => setHistoryDialog({ open: false, row: null })}
        commission={historyDialog.row}
        userRole={USER_ROLES.CLIENT}
      />

      <Dialog open={dialog.open} onClose={closeDialog}>
        <DialogTitle>{dialog.mode === 'decline' ? 'Decline Commission' : 'Edit Commission Decision'}</DialogTitle>
        <DialogContent>
          <Typography variant='body2' mb={1}>
            Please provide a reason for declining. This will be shared with the lead agent.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label='Reason'
            value={dialog.reason}
            onChange={(e) => setDialog({ ...dialog, reason: e.target.value, backup: e.target.value })}
          />
          <Button onClick={dialog.cleared ? restoreReason : clearReason} size='small' sx={{ mt: 1 }}>
            {dialog.cleared ? 'Restore' : 'Clear'}
          </Button>
        </DialogContent>
        <DialogActions>
          <Button disabled={isApproving || isDeclining} onClick={closeDialog}>
            Cancel
          </Button>
          {dialog.mode === 'edit' && dialog.row?.status !== COMMISSION_STATUS.APPROVED && (
            <Button
              startIcon={isApproving ? <CircularProgress size={18} /> : null}
              disabled={isApproving || isDeclining}
              onClick={handleApprove}
              variant='contained'
              color='success'
            >
              Approve
            </Button>
          )}
          <Button
            startIcon={isDeclining ? <CircularProgress size={18} /> : null}
            disabled={isApproving || isDeclining || !dialog.reason.trim()}
            onClick={handleDecline}
            variant='contained'
            color='error'
          >
            Decline
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClientCommissionOverview;
